/* ==========================================================================
 * bs-preorder.js - de gedeelde pre-orderdialoog (snippets/bs-preorder.liquid)
 * --------------------------------------------------------------------------
 * Een maat zonder voorraad is niet koopbaar (DENY), maar de klant kan hem wel
 * als pre-order AANVRAGEN. Dit bestand doet drie dingen:
 *
 *   1. het kent de voorraad per variant (uit het JSON-blok dat de snippet uit
 *      Liquid rendert) en rekent uit of een gevraagd aantal past;
 *   2. het levert de meldingsteksten (M1 tot en met M8) met knoppen;
 *   3. het opent de dialoog met het contactformulier, voorgevuld.
 *
 * Andere secties hoeven hiervoor GEEN eigen JavaScript te schrijven: een knop
 * met [data-bs-po-open] plus data-attributen is genoeg (gebeurtenisdelegatie
 * op document).
 *
 * OPENBARE API (window.BSPreorder)
 * --------------------------------------------------------------------------
 *   voorraad(variantId)      -> number | null    (null = niet gevolgd/onbeperkt/onbekend)
 *   gegevens(variantId)      -> { sku, maat, titel, koopbaar, aantal } | null
 *   inWagen(cart, variantId) -> number           (som van de regels in /cart.js)
 *   controleer({ variantId, gevraagd, inWagen }) -> { soort, opVoorraad, vrij, tekort, inWagen }
 *                               soort: 'ok' | 'uit' | 'deels' | 'vol'
 *   controleerPakket(regels, cart)               -> { soort, regels: [...], eerste }
 *   meldingHtml(uitkomst, opties)                -> HTML-tekst (melding + knoppen)
 *   tekst(sleutel, waarden)  -> string           (teksten uit de snippet)
 *   voegToe(regels, opties)  -> Promise<uitkomst> (winkelwagen, in stukken van 50)
 *
 *   open(opties)             -> boolean          (zie hieronder)
 *   sluit()
 *   verstuur()               -> requestSubmit(), NOOIT submit() (hCaptcha)
 *   ververs()                -> Promise<boolean> (verse voorraad via sections/bs-voorraad)
 *   herlaad()                                    (JSON-blokken opnieuw lezen)
 *
 * open() accepteert twee vormen:
 *   open({ variantId, sku, maat, aantal, tekort, bron })            een maat
 *   open({ regels: [{ variantId, aantal }], meters, diameter, bron }) een pakket
 * Het aantal in het formulier wordt "tekort" als dat is meegegeven, anders
 * "aantal". Bij een pakket is het aantal het aantal meter.
 *
 * VOEGTOE: WAAROM DIT HIER STAAT
 * --------------------------------------------------------------------------
 * Gemeten op 16-09 op birdstopper.nl met variant 59182714913093 (SKU 17913,
 * 91 op voorraad), winkelwagen steeds eerst geleegd:
 *
 *   POST /cart/add.js  {items:[{id,quantity:91}]}  -> 422, "Er zijn slechts 50
 *                       artikelen aan je winkelwagen toegevoegd", wagen 50
 *   POST /cart/add.js  {items:[{id,quantity:50}]}  -> 200, daarna nog eens
 *                       {items:[{id,quantity:41}]} -> 422, "De maximale
 *                       hoeveelheid van dit artikel zit al in je winkelwagen",
 *                       wagen blijft 50
 *   POST /cart/add.js  {id,quantity:91}            -> 200, wagen 91
 *   POST /cart/add.js  {id,quantity:50} en daarna {id,quantity:41} -> 200+200,
 *                       wagen 91
 *
 * De items[]-vorm loopt dus vast op 50 stuks per artikel, ook over meerdere
 * verzoeken heen; de enkelvoudige vorm {id, quantity} niet. voegToe() gebruikt
 * daarom de enkelvoudige vorm en knipt elke regel in stukken van maximaal 50,
 * die hij een voor een verstuurt. Daarna leest hij /cart.js opnieuw, zodat het
 * antwoord zegt wat er ECHT in de winkelwagen ligt en niet wat we vroegen.
 *
 *   voegToe([{ id: 59182714913093, quantity: 91 }], { secties: 'cart-icon-bubble' })
 *     -> { gelukt: true, toegevoegd: { "59182714913093": 91 }, tekort: {},
 *          fout: null, wagen: {...}, antwoord: {...} }
 *
 * opties: { max: 50, secties: 'cart-icon-bubble', melden: false }
 * Bij te weinig voorraad staat het tekort in `tekort` en blijft `fout` leeg
 * (dat is geen storing maar een uitkomst; toon dan melding M4 via
 * meldingHtml('na422', ...)). `fout` is alleen een echte storing.
 * voegToe() werpt nooit en breekt nooit: bij een uitzondering komt er gewoon
 * een uitkomst terug met `fout` gevuld.
 *
 * Alle klantteksten staan in de snippet, niet hier.
 * Bewust zonder afhankelijkheden; dubbel inladen is onschadelijk.
 * ========================================================================== */
(function () {
  'use strict';

  if (window.BSPreorder) { return; }

  var STANDAARDTEKST = {
    knop: 'Pre-order aanvragen',
    knop_deels_winkelwagen: '{X} in winkelwagen',
    knop_deels_preorder: 'Pre-order aanvragen voor {Y}',
    m1: 'Deze maat is tijdelijk uitverkocht. Je kunt hem wel als pre-order aanvragen. Levering kan maximaal 4 weken duren.',
    m2: 'Van deze maat hebben we er nog {X} op voorraad. Zet die alvast in je winkelwagen en vraag de overige {Y} aan als pre-order. Levering van de pre-order kan maximaal 4 weken duren.',
    m3: 'Alles wat we van deze maat op voorraad hebben, ligt al in je winkelwagen. Meer nodig? Vraag een pre-order aan. Levering kan maximaal 4 weken duren.',
    m4: 'Er zijn minder op voorraad dan je vroeg. {Z} staan nu in je winkelwagen. Vraag voor de rest een pre-order aan, levering kan maximaal 4 weken duren.',
    m4_niets: 'Er is niets toegevoegd, want deze maat is net uitverkocht. Vraag een pre-order aan, levering kan maximaal 4 weken duren.',
    m5: 'Voor dit pakket hebben we niet genoeg op voorraad. Van {maat} hebben we er nog {X}. Vraag het pakket aan als pre-order, dan nemen we contact met je op. Levering kan maximaal 4 weken duren.',
    m6: 'Deze diameter is tijdelijk uitverkocht. Je kunt je {m} meter als pre-order aanvragen. Levering kan maximaal 4 weken duren.',
    m7: 'Meer nodig dan we op voorraad hebben? Vraag een pre-order aan.',
    m8: 'Er zijn nog maar {X} op voorraad. Pas het aantal aan en vraag voor de rest een pre-order aan.',
    veldfout_naam: 'Vul je naam in.',
    veldfout_aantal: 'Vul een aantal van 1 of meer in.',
    bezig: 'Bezig met versturen',
    voorraad_uit: 'uitverkocht',
    voorraad_op: '{X} op voorraad'
  };

  /* Klantteksten uit window.BSTekst (snippets/bs-teksten.liquid, sleutels bs.* in de
     locale-bestanden). Ontbreekt een tekst, dan de Nederlandse terugval die hier
     in de code staat. {naam} in een tekst wordt ingevuld met waarden.naam. */
  function tk(sleutel, terug, waarden) {
    var bron = window.BSTekst;
    var s = (bron && typeof bron[sleutel] === 'string' && bron[sleutel] !== '' && bron[sleutel].indexOf('translation missing') !== 0) ? bron[sleutel] : terug;
    if (!waarden) { return s; }
    return String(s).replace(/\{(\w+)\}/g, function (heel, naam) {
      return Object.prototype.hasOwnProperty.call(waarden, naam) ? String(waarden[naam]) : heel;
    });
  }

  var voorraadKaart = null;
  var tekstKaart = null;
  var laatsteFocus = null;

  /* ----------------------------------------------------------------------
   * 1. GEREEDSCHAP
   * -------------------------------------------------------------------- */

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function alles(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function getal(w) {
    var n = parseInt(w, 10);
    return isFinite(n) ? n : 0;
  }

  function wortel() {
    if (window.BBWinkel && window.BBWinkel.wortel) { return window.BBWinkel.wortel(); }
    var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.charAt(r.length - 1) === '/' ? r : r + '/';
  }

  /** Voegt alle JSON-blokken met deze selector samen tot een object. */
  function leesJson(sel) {
    var uit = {};
    alles(sel).forEach(function (el) {
      var o;
      try { o = JSON.parse(el.textContent || '{}'); } catch (e) { o = null; }
      if (!o || typeof o !== 'object') { return; }
      Object.keys(o).forEach(function (k) { uit[k] = o[k]; });
    });
    return uit;
  }

  function kaart() {
    if (!voorraadKaart) { voorraadKaart = leesJson('[data-bs-po-voorraad]'); }
    return voorraadKaart;
  }

  function teksten() {
    if (!tekstKaart) {
      var uit = {};
      Object.keys(STANDAARDTEKST).forEach(function (k) { uit[k] = STANDAARDTEKST[k]; });
      var uitSnippet = leesJson('[data-bs-po-teksten]');
      Object.keys(uitSnippet).forEach(function (k) { uit[k] = uitSnippet[k]; });
      tekstKaart = uit;
    }
    return tekstKaart;
  }

  /**
   * Tekst uit de snippet, met {X}, {Y}, {Z}, {maat}, {d}, {m} ingevuld.
   * De teksten mogen <b> bevatten; de ingevulde waarden worden ontsmet.
   */
  function tekst(sleutel, waarden) {
    var s = teksten()[sleutel];
    if (s == null) { return ''; }
    if (!waarden) { return s; }
    return String(s).replace(/\{(\w+)\}/g, function (heel, naam) {
      return Object.prototype.hasOwnProperty.call(waarden, naam) ? esc(waarden[naam]) : heel;
    });
  }

  function dialoog() {
    return document.querySelector('[data-bs-po]');
  }

  /* ----------------------------------------------------------------------
   * 2. VOORRAAD EN CONTROLE
   * -------------------------------------------------------------------- */

  function gegevens(variantId) {
    if (variantId == null) { return null; }
    var r = kaart()[String(variantId)];
    return r || null;
  }

  /** Aantal op voorraad, of null als het niet gevolgd wordt of onbekend is. */
  function voorraad(variantId) {
    var r = gegevens(variantId);
    if (!r) { return null; }
    return (typeof r.aantal === 'number') ? r.aantal : null;
  }

  /** Hoeveel van deze variant al in de winkelwagen ligt (antwoord van /cart.js). */
  function inWagen(cart, variantId) {
    var regels = (cart && cart.items) || [];
    var id = String(variantId);
    var n = 0;
    regels.forEach(function (regel) {
      if (String(regel.id) === id || String(regel.variant_id) === id) { n += (regel.quantity || 0); }
    });
    return n;
  }

  /**
   * Past het gevraagde aantal? Zie onderzoek/pre-order.md hoofdstuk 4.6.
   * Zonder voorraadgetal (null) is het altijd 'ok': liever doorlaten dan
   * onterecht blokkeren; de 422-vangnet vangt de rest.
   */
  function controleer(opties) {
    var o = opties || {};
    var gevraagd = Math.max(1, getal(o.gevraagd) || 1);
    var al = Math.max(0, getal(o.inWagen));
    var r = gegevens(o.variantId);
    var n = r && typeof r.aantal === 'number' ? r.aantal : null;

    if (!r || n === null) {
      return { soort: 'ok', opVoorraad: null, vrij: null, tekort: 0, inWagen: al, gevraagd: gevraagd, variantId: o.variantId };
    }
    var vrij = Math.max(0, n - al);
    if (r.koopbaar === false || n === 0) {
      return { soort: 'uit', opVoorraad: 0, vrij: 0, tekort: gevraagd, inWagen: al, gevraagd: gevraagd, variantId: o.variantId };
    }
    if (vrij === 0) {
      return { soort: 'vol', opVoorraad: n, vrij: 0, tekort: gevraagd, inWagen: al, gevraagd: gevraagd, variantId: o.variantId };
    }
    if (gevraagd > vrij) {
      return { soort: 'deels', opVoorraad: vrij, vrij: vrij, tekort: gevraagd - vrij, inWagen: al, gevraagd: gevraagd, variantId: o.variantId };
    }
    return { soort: 'ok', opVoorraad: vrij, vrij: vrij, tekort: 0, inWagen: al, gevraagd: gevraagd, variantId: o.variantId };
  }

  /**
   * Hetzelfde voor een pakket (keuzehulp, paneelhulp).
   * regels: [{ variantId, aantal }]. Het pakket is 'ok' als elke regel ok is.
   * `eerste` is de eerste regel die niet past (handig voor melding M5).
   */
  function controleerPakket(regels, cart) {
    var uit = [];
    var eerste = null;
    (regels || []).forEach(function (regel) {
      var u = controleer({
        variantId: regel.variantId != null ? regel.variantId : regel.id,
        gevraagd: regel.aantal != null ? regel.aantal : regel.quantity,
        inWagen: cart ? inWagen(cart, regel.variantId != null ? regel.variantId : regel.id) : 0
      });
      uit.push(u);
      if (!eerste && u.soort !== 'ok') { eerste = u; }
    });
    return { soort: eerste ? eerste.soort : 'ok', regels: uit, eerste: eerste };
  }

  /* ----------------------------------------------------------------------
   * 3. MELDINGEN
   * -------------------------------------------------------------------- */

  function knopHtml(etiket, data, klasse) {
    var attrs = '';
    Object.keys(data || {}).forEach(function (k) {
      if (data[k] == null || data[k] === '') { return; }
      attrs += ' data-' + k + '="' + esc(data[k]) + '"';
    });
    return '<button type="button" class="' + (klasse || 'bs-knop bs-knop--twee bs-knop--klein') + '"' + attrs + '>' + esc(etiket) + '</button>';
  }

  /**
   * HTML voor de melding bij een uitkomst.
   *
   * @param {object|string} uitkomst  uitkomst van controleer(), of een van de
   *        codes 'uit' | 'deels' | 'vol' | 'na422' | 'pakket' | 'leeg' |
   *        'wagenmax' | 'wagenteveel'
   * @param {object} [opties] { variantId, bron, gevraagd, tekort, opVoorraad,
   *        inWagenNu, maat, meters, diameter, pakket, knoppen: false }
   */
  function meldingHtml(uitkomst, opties) {
    var o = opties || {};
    var u = (typeof uitkomst === 'string') ? { soort: uitkomst } : (uitkomst || {});
    var soort = o.soort || u.soort || 'uit';
    var variantId = o.variantId != null ? o.variantId : u.variantId;
    var bron = o.bron || '';
    var gevraagd = o.gevraagd != null ? o.gevraagd : u.gevraagd;
    var tekort = o.tekort != null ? o.tekort : u.tekort;
    var opVoorraad = o.opVoorraad != null ? o.opVoorraad : u.opVoorraad;
    var maat = o.maat || (gegevens(variantId) || {}).maat || '';
    var basis = { 'bs-po-open': '1', 'variant-id': variantId, 'bron': bron, 'maat': o.maat, 'aantal': gevraagd, 'tekort': tekort };
    var body = '';
    var knoppen = '';

    if (soort === 'uit') {
      body = tekst('m1');
      knoppen = knopHtml(tekst('knop'), basis);
    } else if (soort === 'deels') {
      body = tekst('m2', { X: opVoorraad, Y: tekort });
      knoppen =
        knopHtml(tekst('knop_deels_winkelwagen', { X: opVoorraad }),
          { 'bs-po-actie': 'winkelwagen', 'variant-id': variantId, 'aantal': opVoorraad },
          'bs-knop bs-knop--klein') +
        knopHtml(tekst('knop_deels_preorder', { Y: tekort }),
          { 'bs-po-open': '1', 'variant-id': variantId, 'bron': bron, 'maat': o.maat, 'aantal': gevraagd, 'tekort': tekort, 'voorraad': opVoorraad });
    } else if (soort === 'vol') {
      body = tekst('m3');
      knoppen = knopHtml(tekst('knop'), basis);
    } else if (soort === 'na422') {
      var nu = getal(o.inWagenNu);
      body = nu > 0 ? tekst('m4', { Z: nu }) : tekst('m4_niets');
      knoppen = knopHtml(tekst('knop'), basis);
    } else if (soort === 'pakket') {
      body = tekst('m5', { maat: maat, X: opVoorraad });
      knoppen = knopHtml(tekst('knop'), {
        'bs-po-open': '1', 'bron': bron, 'pakket': o.pakket, 'meters': o.meters, 'diameter': o.diameter, 'aantal': o.meters
      });
    } else if (soort === 'leeg') {
      body = tekst('m6', { d: o.diameter, m: o.meters });
      knoppen = knopHtml(tekst('knop'), {
        'bs-po-open': '1', 'bron': bron, 'meters': o.meters, 'diameter': o.diameter, 'aantal': o.meters
      });
    } else if (soort === 'wagenmax') {
      body = tekst('m7');
      knoppen = knopHtml(tekst('knop'), basis);
    } else if (soort === 'wagenteveel') {
      body = tekst('m8', { X: opVoorraad });
      knoppen = knopHtml(tekst('knop'), basis);
    } else {
      return '';
    }

    return '<div class="bs-po-melding bs-vlak-zacht" data-bs-po-melding>' +
      '<p class="bs-po-melding-tekst">' + body + '</p>' +
      (o.knoppen === false ? '' : '<div class="bs-po-melding-knoppen">' + knoppen + '</div>') +
      '</div>';
  }

  /* ----------------------------------------------------------------------
   * 4. DE DIALOOG
   * -------------------------------------------------------------------- */

  function zetVeld(dlg, naam, waarde) {
    var el = dlg.querySelector('[data-bs-po-veld="' + naam + '"]');
    if (!el) { return; }
    if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') { el.value = waarde == null ? '' : waarde; }
    else { el.textContent = waarde == null ? '' : waarde; }
  }

  /* De regels van een pakket, zonder "Pakket:" ervoor (zo gaan ze ook naar contact[Pakket]). */
  function pakketRegels(o, regels) {
    var stukken = regels.map(function (r) {
      var g = gegevens(r.variantId) || {};
      return (r.aantal || 1) + 'x ' + (g.maat || r.maat || tk('po_onbekend', 'onbekend')) + (g.sku ? ' (' + g.sku + ')' : '');
    });
    var staart = '';
    if (o.meters) { staart += ', ' + tk('po_samen', 'samen {m} meter', { m: o.meters }); }
    if (o.diameter) { staart += ' Ø' + o.diameter + ' cm'; }
    return stukken.join('; ') + staart;
  }

  function samenvatting(o, regels) {
    if (regels && regels.length) {
      return tk('po_pakket', 'Pakket: {pakket}', { pakket: pakketRegels(o, regels) });
    }
    var g2 = gegevens(o.variantId) || {};
    var maat = o.maat || g2.maat;
    var sku = o.sku || g2.sku;
    if (!maat && !sku) { return ''; }
    return tk('po_maat', 'Maat: {maat}', { maat: maat || tk('po_onbekend', 'onbekend') })
      + (sku ? ' ' + tk('po_artikelnummer', '(artikelnummer {sku})', { sku: sku }) : '');
  }

  function voorraadTekst(o, regels) {
    if (regels && regels.length) {
      return regels.map(function (r) {
        var n = voorraad(r.variantId);
        var g = gegevens(r.variantId) || {};
        return (g.sku || r.variantId) + ': ' + (n === null ? 'onbekend' : (n === 0 ? tekst('voorraad_uit') : tekst('voorraad_op', { X: n })));
      }).join('; ');
    }
    if (o.voorraad != null && o.voorraad !== '') { return tekst('voorraad_op', { X: o.voorraad }); }
    var n2 = voorraad(o.variantId);
    if (n2 === null) { return ''; }
    return n2 === 0 ? tekst('voorraad_uit') : tekst('voorraad_op', { X: n2 });
  }

  function focusbaar(dlg) {
    return alles('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])', dlg)
      .filter(function (el) { return el.offsetParent !== null || el === document.activeElement; });
  }

  function open(opties) {
    var dlg = dialoog();
    if (!dlg) { return false; }
    var o = opties || {};
    var regels = o.regels && o.regels.length ? o.regels : null;
    var pakketTekst = o.pakket || (regels ? pakketRegels(o, regels) : '');

    /* Het aantal in het formulier: liefst het tekort, anders het gevraagde
       aantal, anders het aantal meter (keuzehulp en paneelhulp), anders 1. */
    var aantal = o.tekort != null && o.tekort !== '' ? getal(o.tekort) : getal(o.aantal);
    if (!aantal || aantal < 1) { aantal = getal(o.meters) || 1; }

    zetVeld(dlg, 'maat', regels ? '' : (o.maat || (gegevens(o.variantId) || {}).maat || ''));
    zetVeld(dlg, 'sku', regels
      ? regels.map(function (r) { return (gegevens(r.variantId) || {}).sku || r.sku || ''; }).filter(Boolean).join(', ')
      : (o.sku || (gegevens(o.variantId) || {}).sku || ''));
    zetVeld(dlg, 'pakket', pakketTekst);
    zetVeld(dlg, 'voorraad', voorraadTekst(o, regels));
    zetVeld(dlg, 'aantal', aantal);
    zetVeld(dlg, 'aantal-etiket', regels || o.meters ? tk('po_aantal_meter', 'Aantal meter') : tk('aantal', 'Aantal'));

    var pagina = dlg.querySelector('[data-bs-po-veld="pagina"]');
    if (pagina) {
      var bron = o.bron || dlg.getAttribute('data-bron') || 'Onbekend';
      pagina.value = bron + ' (' + (window.location.pathname || '') + ')';
    }

    var sam = dlg.querySelector('[data-bs-po-veld="samenvatting"]');
    if (sam) {
      var s = samenvatting(o, regels);
      sam.textContent = s;
      sam.hidden = !s;
    }

    laatsteFocus = document.activeElement;
    toon(dlg);

    var eerste = dlg.querySelector('#bs-po-naam') || focusbaar(dlg)[0];
    if (eerste) { try { eerste.focus(); } catch (e) { /* geen focus is geen fout */ } }
    return true;
  }

  function toon(dlg) {
    if (dlg.open) { return; }
    if (typeof dlg.showModal === 'function') {
      try { dlg.showModal(); } catch (e) { dlg.setAttribute('open', ''); }
    } else {
      dlg.setAttribute('open', '');
    }
    document.documentElement.classList.add('bs-po-aan');
  }

  function sluit() {
    var dlg = dialoog();
    if (!dlg) { return; }
    if (typeof dlg.close === 'function' && dlg.open) {
      try { dlg.close(); } catch (e) { dlg.removeAttribute('open'); }
    } else {
      dlg.removeAttribute('open');
    }
    document.documentElement.classList.remove('bs-po-aan');
    if (laatsteFocus && laatsteFocus.focus) {
      try { laatsteFocus.focus(); } catch (e) { /* weg is weg */ }
    }
    laatsteFocus = null;
  }

  /** Versturen zoals de klant op de knop drukt, zodat hCaptcha meedoet. */
  function verstuur() {
    var f = document.getElementById('bs-po-form');
    if (!f) { return false; }
    if (typeof f.requestSubmit === 'function') { f.requestSubmit(); return true; }
    var knop = f.querySelector('[data-bs-po-verstuur]');
    if (knop) { knop.click(); return true; }
    return false;     /* form.submit() nooit: dat slaat hCaptcha over */
  }

  /* ----------------------------------------------------------------------
   * 5. TOEVOEGEN AAN DE WINKELWAGEN, IN STUKKEN VAN MAXIMAAL 50
   * -------------------------------------------------------------------- */

  var MAX_PER_VERZOEK = 50;   /* gemeten grens van de items[]-vorm, zie de kop */

  /** /cart.js lezen. Lukt het niet, dan null; dit mag nooit werpen. */
  function wagenLezen() {
    try {
      return fetch(wortel() + 'cart.js', {
        method: 'GET', credentials: 'same-origin', headers: { Accept: 'application/json' }
      }).then(function (a) {
        return a && a.ok ? a.json() : null;
      }).catch(function () { return null; });
    } catch (e) {
      return Promise.resolve(null);
    }
  }

  /**
   * Een enkel verzoek naar /cart/add.js in de vorm {id, quantity}.
   * Werpt nooit: het antwoord is altijd { ok, status, uitslag, bericht }.
   */
  function eenAdd(id, aantal, secties) {
    var body = { id: id, quantity: aantal };
    if (secties) { body.sections = secties; }
    try {
      return fetch(wortel() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        credentials: 'same-origin',
        body: JSON.stringify(body)
      }).then(function (a) {
        return a.json().catch(function () { return {}; }).then(function (j) {
          return {
            ok: !!a.ok, status: a.status, uitslag: j,
            bericht: (j && (j.description || j.message)) || ''
          };
        });
      }).catch(function (e) {
        return { ok: false, status: 0, uitslag: {}, bericht: (e && e.message) || tk('po_geen_verbinding', 'geen verbinding') };
      });
    } catch (e) {
      return Promise.resolve({ ok: false, status: 0, uitslag: {}, bericht: (e && e.message) || tk('po_geen_verbinding', 'geen verbinding') });
    }
  }

  /**
   * Regels in de winkelwagen leggen. Zie de kop van dit bestand voor de meting
   * waarop de stukken van 50 berusten.
   *
   * @param {array}  regels  [{ id, quantity }]  (variantId/aantal mag ook)
   * @param {object} [opties]
   *        max     {number}  stuk per verzoek, standaard 50
   *        secties {string}  komma-lijst voor Shopify, bijvoorbeeld 'cart-icon-bubble'
   *        melden  {boolean} true: BBWinkel.meldWijziging() na afloop (standaard false,
   *                          de aanroeper doet dat meestal zelf met het juiste antwoord)
   * @returns {Promise<object>} lost ALTIJD op, nooit een afwijzing:
   *   gelukt     {boolean}  alles wat gevraagd is ligt er ook echt in
   *   toegevoegd {object}   { "<variant-id>": aantal }  wat er echt bij kwam
 *                          (bij een weigering het verschil in /cart.js)
   *   tekort     {object}   { "<variant-id>": aantal }  alleen wat er ontbreekt
   *   fout       {object|null} { status, variantId, bericht } bij een ECHTE storing
   *                          (netwerk of een andere status dan 422); te weinig
   *                          voorraad is geen fout maar een tekort
   *   wagen      {object|null} het verse /cart.js
   *   antwoord   {object|null} het laatste geslaagde antwoord van /cart/add.js
   *                          (met `sections` als je die vroeg)
   */
  function voegToe(regels, opties) {
    var uit = { gelukt: false, toegevoegd: {}, tekort: {}, fout: null, wagen: null, antwoord: null };
    try {
      var o = opties || {};
      var max = Math.max(1, getal(o.max) || MAX_PER_VERZOEK);
      var secties = o.secties || null;

      /* Regels opschonen en dubbele varianten samenvoegen. */
      var lijst = [];
      (regels || []).forEach(function (r) {
        if (!r) { return; }
        var id = (r.id != null && r.id !== '') ? r.id : r.variantId;
        var n = getal(r.quantity != null ? r.quantity : r.aantal);
        if (id == null || id === '' || n < 1) { return; }
        var al = null;
        lijst.forEach(function (x) { if (x.id === String(id)) { al = x; } });
        /* De sleutel is altijd tekst (voor de kaarten), het verzoek krijgt het
           getal terug, want zo stuurde het thema het altijd al. */
        var ruw = (typeof id === 'number') ? id : (/^\d+$/.test(String(id)) ? Number(id) : id);
        if (al) { al.aantal += n; } else { lijst.push({ id: String(id), ruw: ruw, aantal: n, gestopt: false }); }
      });
      if (!lijst.length) { uit.gelukt = true; return Promise.resolve(uit); }

      var voor = null;          /* wat er voor de actie al in lag, per variant */
      var geteld = {};          /* terugval als /cart.js niet te lezen is */
      lijst.forEach(function (r) { geteld[r.id] = 0; });

      return wagenLezen().then(function (w) {
        if (w) {
          voor = {};
          lijst.forEach(function (r) { voor[r.id] = inWagen(w, r.id); });
        }
        var ketting = Promise.resolve();
        lijst.forEach(function (r) {
          var rest = r.aantal;
          while (rest > 0) {
            (function (stuk) {
              ketting = ketting.then(function () {
                if (uit.fout || r.gestopt) { return null; }   /* niets meer sturen */
                return eenAdd(r.ruw, stuk, secties).then(function (a) {
                  if (a.ok) {
                    geteld[r.id] += stuk;
                    uit.antwoord = a.uitslag;
                    return null;
                  }
                  if (a.status === 422) { r.gestopt = true; return null; }  /* voorraad op */
                  uit.fout = { status: a.status, variantId: r.id, bericht: a.bericht };
                  return null;
                });
              });
            }(Math.min(max, rest)));
            rest -= Math.min(max, rest);
          }
        });
        return ketting;
      }).then(function () {
        return wagenLezen();
      }).then(function (w) {
        uit.wagen = w || null;
        lijst.forEach(function (r) {
          /* Kwam er geen weigering, dan ligt er precies in wat de geslaagde
             verzoeken zeiden: /cart/add.js is per verzoek alles of niets.
             Weigerde Shopify wel, dan is het verschil in /cart.js de waarheid;
             is de wagen niet te lezen, dan tellen we de geslaagde stukken. */
          var gemeten = (w && voor) ? Math.max(0, inWagen(w, r.id) - voor[r.id]) : null;
          var n = (r.gestopt && gemeten !== null) ? gemeten : geteld[r.id];
          uit.toegevoegd[r.id] = n;
          if (n < r.aantal) { uit.tekort[r.id] = r.aantal - n; }
        });
        uit.gelukt = !uit.fout && !Object.keys(uit.tekort).length;
        if (o.melden && w && window.BBWinkel && typeof window.BBWinkel.meldWijziging === 'function') {
          try { window.BBWinkel.meldWijziging(uit.antwoord || w); } catch (e) { /* de teller is geen voorwaarde */ }
        }
        return uit;
      }).catch(function (e) {
        uit.fout = uit.fout || { status: 0, variantId: null, bericht: (e && e.message) || tk('po_onbekende_fout', 'onbekende fout') };
        uit.gelukt = false;
        return uit;
      });
    } catch (e) {
      uit.fout = { status: 0, variantId: null, bericht: (e && e.message) || tk('po_onbekende_fout', 'onbekende fout') };
      return Promise.resolve(uit);
    }
  }

  /* ----------------------------------------------------------------------
   * 6. VERSE VOORRAAD (sections/bs-voorraad.liquid)
   * -------------------------------------------------------------------- */

  function herlaad() {
    voorraadKaart = null;
    tekstKaart = null;
    kaart();
    teksten();
  }

  function ververs() {
    return fetch(wortel() + '?sections=bs-voorraad', { credentials: 'same-origin' })
      .then(function (a) { return a.ok ? a.json() : null; })
      .then(function (j) {
        var html = j && j['bs-voorraad'];
        if (!html) { return false; }
        var doos = document.createElement('div');
        doos.innerHTML = html;
        var blok = doos.querySelector('[data-bs-po-voorraad]');
        if (!blok) { return false; }
        var nieuw;
        try { nieuw = JSON.parse(blok.textContent || '{}'); } catch (e) { return false; }
        if (!voorraadKaart) { kaart(); }
        Object.keys(nieuw).forEach(function (k) { voorraadKaart[k] = nieuw[k]; });
        var doel = document.querySelector('[data-bs-po-voorraad]');
        if (doel) { doel.textContent = JSON.stringify(voorraadKaart); }
        return true;
      })
      .catch(function () { return false; });
  }

  /* ----------------------------------------------------------------------
   * 7. OPSTARTEN
   * -------------------------------------------------------------------- */

  function opruimen() {
    /* Twee keer gerenderd? De voorraad samenvoegen en de extra dialogen weg,
       anders staan er dubbele id's en twee contactformulieren op de pagina. */
    var dialogen = alles('[data-bs-po]');
    if (dialogen.length < 2) { return; }
    kaart();
    dialogen.slice(1).forEach(function (el) {
      if (el.parentNode) { el.parentNode.removeChild(el); }
    });
    if (window.console && console.warn) {
      console.warn('bs-preorder: de snippet staat meer dan een keer op deze pagina; de extra dialogen zijn verwijderd.');
    }
  }

  function valideer(f) {
    var naam = f.querySelector('#bs-po-naam');
    var aantal = f.querySelector('#bs-po-aantal');
    if (naam && !String(naam.value).trim()) { return { veld: naam, tekst: tekst('veldfout_naam') }; }
    if (aantal && !(getal(aantal.value) >= 1)) { return { veld: aantal, tekst: tekst('veldfout_aantal') }; }
    return null;
  }

  function toonVeldfout(f, fout) {
    var plek = f.querySelector('[data-bs-po-veldfout]');
    if (plek) {
      plek.textContent = fout ? fout.tekst : '';
      plek.hidden = !fout;
    }
    if (fout && fout.veld && fout.veld.focus) { try { fout.veld.focus(); } catch (e) { /* niets */ } }
  }

  function start() {
    opruimen();
    var dlg = dialoog();
    if (!dlg) { return; }

    /* Dankmelding of foutmelding na het versturen: dialoog meteen openen. */
    var uitkomst = dlg.querySelector('[data-bs-po-uitkomst]');
    if (uitkomst) {
      dlg.setAttribute('data-bs-po-status', uitkomst.getAttribute('data-bs-po-uitkomst'));
      laatsteFocus = null;
      toon(dlg);
      var kop = dlg.querySelector('.bs-po-kop');
      if (kop) { kop.setAttribute('tabindex', '-1'); try { kop.focus(); } catch (e) { /* niets */ } }
    }

    var f = document.getElementById('bs-po-form');
    if (f) {
      f.addEventListener('submit', function (e) {
        var fout = valideer(f);
        if (fout) {
          e.preventDefault();
          e.stopPropagation();          /* niet doorgeven aan de captcha-bootstrap */
          toonVeldfout(f, fout);
          return;
        }
        toonVeldfout(f, null);
        var knop = f.querySelector('[data-bs-po-verstuur]');
        if (knop) { knop.setAttribute('aria-disabled', 'true'); knop.textContent = tekst('bezig'); }
      });
    }

    /* Esc en klikken op de achtergrond sluiten de dialoog. */
    dlg.addEventListener('cancel', function () {
      document.documentElement.classList.remove('bs-po-aan');
    });
    dlg.addEventListener('close', function () {
      document.documentElement.classList.remove('bs-po-aan');
    });
    dlg.addEventListener('click', function (e) {
      if (e.target === dlg) { sluit(); }
    });
    dlg.addEventListener('keydown', function (e) {
      /* Esc sluit altijd zelf, ook al doet een <dialog> in showModal-stand dat
         normaal van zichzelf: zo werkt het ook in de terugvalstand en is het
         te meten. sluit() zet daarna de focus terug. */
      if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); sluit(); return; }
      if (e.key !== 'Tab') { return; }
      var lijst = focusbaar(dlg);
      if (!lijst.length) { return; }
      var eerste = lijst[0];
      var laatste = lijst[lijst.length - 1];
      if (e.shiftKey && document.activeElement === eerste) { e.preventDefault(); laatste.focus(); }
      else if (!e.shiftKey && document.activeElement === laatste) { e.preventDefault(); eerste.focus(); }
    });
  }

  /* Gebeurtenisdelegatie: elke knop met [data-bs-po-open] opent de dialoog.
     Zo hoeven Alle maten, de winkelwagen en de keuzehulp niets zelf te doen. */
  document.addEventListener('click', function (e) {
    var knop = e.target && e.target.closest ? e.target.closest('[data-bs-po-open]') : null;
    if (knop) {
      e.preventDefault();
      var d = knop.dataset || {};
      open({
        variantId: d.variantId,
        sku: d.sku,
        maat: d.maat,
        aantal: d.aantal,
        tekort: d.tekort,
        voorraad: d.voorraad,
        pakket: d.pakket,
        meters: d.meters,
        diameter: d.diameter,
        bron: d.bron
      });
      return;
    }
    var dicht = e.target && e.target.closest ? e.target.closest('[data-bs-po-sluit]') : null;
    if (dicht) { e.preventDefault(); sluit(); }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  window.BSPreorder = {
    voorraad: voorraad,
    gegevens: gegevens,
    inWagen: inWagen,
    controleer: controleer,
    controleerPakket: controleerPakket,
    meldingHtml: meldingHtml,
    tekst: tekst,
    open: open,
    sluit: sluit,
    verstuur: verstuur,
    voegToe: voegToe,
    ververs: ververs,
    herlaad: herlaad
  };
}());
